import { useQuery } from '@apollo/client/react';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { GET_USER } from '../../../lib/graphql/queries/organizations';

export type UserRole = 'super_admin' | 'org_admin' | 'agent';

// Maps every role we've ever shipped onto the current three-role contract.
// Why: legacy Firestore/Firebase-claim roles ('user', 'admin', 'Broker',
// 'team_lead'…) still leak into ID tokens. SPEC-1xxx — without this
// mapping, GraphQL users with stale claims fall through to `null` here
// and get treated as roleless, which silently downgrades their UI access.
// Frontend collapses org_admin/agent/legacy into the same surface area;
// only `super_admin` gets Client Administration. Backend still enforces
// finer-grained permissions.
const normalizeUserRole = (role?: string | null): UserRole | null => {
  if (!role) return null;

  const normalized = role.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) return null;

  if (normalized === 'super_admin') return 'super_admin';
  if (normalized === 'org_admin' || normalized === 'admin') return 'org_admin';
  if (normalized === 'agent') return 'agent';

  // Legacy values ('user', 'broker', 'team_lead', 'team_leader', …) and any
  // future unknown role default to `agent` — least privilege, not blocked.
  return 'agent';
};

export interface UserPreferences {
  uiHints?: {
    newDesignIntroVersion?: number;
  };
}

export interface User {
  id: string;
  email: string;
  organizationId: string;
  role: UserRole | null;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  photoURL?: string;
  phoneNumber?: string;
  bio?: string;
  slug?: string;
  preferences?: UserPreferences;
}

interface GQLUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  photoUrl: string;
  role: string;
  status: string;
  primaryOrganizationId: string;
  bio?: string | null;
  slug?: string | null;
  preferences?: UserPreferences;
}

interface UseUserReturn {
  user: User | null;
  hasLoaded: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canManageUsers: boolean;
  canManageTeamMembers: boolean;
  loading: boolean;
  error: Error | null;
}

export const useUser = (): UseUserReturn => {
  const { user: authUser, role: authRole, loading: authLoading } = useAuthStore();

  const { data, loading: queryLoading, error } = useQuery<{ user: GQLUser | null }>(GET_USER, {
    variables: { firebaseAuthId: authUser?.uid ?? '' },
    skip: !authUser?.uid || authLoading,
    fetchPolicy: 'cache-first',
  });

  const gql = data?.user ?? null;
  const normalizedGraphRole = normalizeUserRole(gql?.role);
  const normalizedAuthRole = normalizeUserRole(authRole);
  const effectiveRole = normalizedGraphRole ?? normalizedAuthRole;

  const user: User | null = gql
    ? {
        id: gql.userId,
        email: gql.email,
        organizationId: gql.primaryOrganizationId,
        role: effectiveRole,
        firstName: gql.firstName,
        lastName: gql.lastName,
        displayName: `${gql.firstName} ${gql.lastName}`.trim() || gql.email,
        photoURL: gql.photoUrl,
        phoneNumber: gql.phone,
        bio: gql.bio ?? undefined,
        slug: gql.slug ?? undefined,
        preferences: gql.preferences,
      }
    : null;

  const isSuperAdmin = effectiveRole === 'super_admin';
  const isAdmin = isSuperAdmin || effectiveRole === 'org_admin';

  return {
    user,
    hasLoaded: data !== undefined,
    isAdmin,
    isSuperAdmin,
    canManageUsers: isSuperAdmin,
    canManageTeamMembers: isAdmin,
    loading: authLoading || queryLoading,
    error: (error as Error | null) ?? null,
  };
};

export const useRoleAccess = ({
  requiredRole,
  requiredPermission,
}: {
  requiredRole?: UserRole;
  requiredPermission?: 'manageUsers' | 'manageTeamMembers';
} = {}): boolean => {
  const { user, isAdmin, isSuperAdmin, canManageUsers, canManageTeamMembers } = useUser();

  if (!user) return false;

  if (requiredRole) {
    if (requiredRole === 'super_admin' && !isSuperAdmin) return false;
    if (requiredRole === 'org_admin' && !isAdmin) return false;
    // requiredRole === 'agent' — any logged-in user qualifies; the !user guard above handles the empty case.
  }

  if (requiredPermission) {
    if (requiredPermission === 'manageUsers' && !canManageUsers) return false;
    if (requiredPermission === 'manageTeamMembers' && !canManageTeamMembers) return false;
  }

  return true;
};
